'use client';
import { GroqImage } from '@/helpers/Image';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';

interface IPhilosophy {
  label: string;
  name: string;
  title: PortableTextBlock[];
  description: PortableTextBlock[];
  image: GROQImage;
  button: GROQButton;
}

export default function Philosophy({
  label,
  name,
  title,
  description,
  image,
  button,
}: IPhilosophy) {
  const isMobile = useIsMobile();

  return (
    <section className="flex flex-col gap-[8rem] lg:gap-[18rem] py-[4rem] lg:py-[24.75rem] overflow-x-clip">
      <div className="container max-w-[190rem]">
        <motion.div
          initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="flex flex-wrap w-full justify-between items-end gap-[4rem] lg:gap-[18rem]"
        >
          <motion.div className="flex lg:w-[90rem] flex-col gap-[3rem] lg:gap-[3.62rem] uppercase text-offwhite lg:prose-h2:leading-[120%] prose-h2:leading-[110%]">
            <p className="font-nunito text-[1.25rem] lg:text-[1.625rem] font-[300] tracking-[0.4125rem] lg:tracking-[0.53625rem] flex flex-wrap items-center gap-[2rem]">
              {label}
              <span className="block h-[.125rem] bg-offwhite w-[6rem] lg:w-[7rem]"></span>
              {name}
            </p>
            {title && (
              <PortableText value={title} components={richTextComponent} />
            )}
          </motion.div>

          <motion.div className="lg:w-[62.75rem]  text-[#f2f0ef]/70 flex flex-col gap-[2rem]">
            {description && (
              <PortableText
                value={description}
                components={richTextComponent}
              />
            )}

            {button && <CTAButton className="hidden lg:block" data={button} />}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: '5rem' }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 3, ease: [0.25, 1, 0.5, 1] }}
        className="w-[calc(100%+5rem)] lg:-translate-x-0 !-translate-x-10 lg:w-full pointer-events-none select-none"
      >
        {image && (
          <GroqImage
            src={image}
            alt={image?.name}
            className="!w-full !h-full !object-contain"
          />
        )}
      </motion.div>
    </section>
  );
}
